let activeLocale = "en-US";
let activeTimezone = "UTC";
let dateTimeFormatter = createDateTimeFormatter(activeLocale, activeTimezone);
let timeFormatter = createTimeFormatter(activeLocale, activeTimezone);
let relativeFormatter = new Intl.RelativeTimeFormat(activeLocale, { numeric: "auto" });

function createDateTimeFormatter(locale: string, timezone: string) {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone: timezone });
}

function createTimeFormatter(locale: string, timezone: string) {
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone: timezone });
}

export function isValidTimezone(value?: string | null): value is string {
  if (!value) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export function configureDateTime(locale?: string | null, timezone?: string | null, fallbackTimezone = "UTC") {
  const nextLocale = locale || "en-US";
  const nextTimezone = isValidTimezone(timezone) ? timezone : fallbackTimezone;
  if (nextLocale === activeLocale && nextTimezone === activeTimezone) return;
  activeLocale = nextLocale;
  activeTimezone = nextTimezone;
  try {
    dateTimeFormatter = createDateTimeFormatter(activeLocale, activeTimezone);
    timeFormatter = createTimeFormatter(activeLocale, activeTimezone);
    relativeFormatter = new Intl.RelativeTimeFormat(activeLocale, { numeric: "auto" });
  } catch {
    activeLocale = "en-US";
    activeTimezone = fallbackTimezone;
    dateTimeFormatter = createDateTimeFormatter(activeLocale, activeTimezone);
    timeFormatter = createTimeFormatter(activeLocale, activeTimezone);
    relativeFormatter = new Intl.RelativeTimeFormat(activeLocale, { numeric: "auto" });
  }
}

export function formatDate(value?: string | number | Date) {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateTimeFormatter.format(date);
}

export function formatTime(value?: string | number | Date) {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : timeFormatter.format(date);
}

export function formatRelative(value?: string | number | Date) {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absolute = Math.abs(seconds);
  if (absolute < 60) return relativeFormatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return relativeFormatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relativeFormatter.format(hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return relativeFormatter.format(days, "day");
  return formatDate(date);
}

export function formatBytes(bytes = 0) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

export function initials(name?: string) {
  if (!name) return "NC";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function ticketLabel(number: string | number) {
  return `#${String(number).replace(/^#/, "")}`;
}

